import { FormControl, MenuItem, Select } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as towerPricingSelectors from "store/selectors/pricing/tower/towerPricingSelectors";
import * as utils from "utils";

const NONE_KEY = "__NONE__";

// A primary tower layer carries no attachment, and shortLayerName would render
// that as the literal "$null". Treat it as zero so the option reads "$25m xs $0".
const layerLabel = (towerLayer) =>
  utils.shortLayerName({
    limit: towerLayer?.limit,
    attachment: towerLayer?.attachment ?? 0,
  });

/**
 * Compact followed-policy picker for use inside a table row.
 *
 * Unlike FollowedPolicySelector — which is form-shaped and always edits the
 * *active* layer — this edits the layer it is given, so a table can offer one
 * per row. Options are the tower shares; the label carries the share's policy
 * number where one has been recorded, because the carrier alone is not always
 * unique within a tower.
 */
const FollowedPolicyTableSelect = ({ layer, disabled }) => {
  const dispatch = useDispatch();
  const towerLayers =
    useSelector(towerPricingSelectors.selectResolvedTower)?.layers ?? [];

  const choices = towerLayers.flatMap((towerLayer) =>
    (towerLayer.shares ?? [])
      .filter((share) => !!share.id)
      .map((share) => ({
        key: share.id,
        name: [
          `${layerLabel(towerLayer)} - ${share.carrier || "?"}`,
          share.reference ? `(${share.reference})` : null,
        ]
          .filter(Boolean)
          .join(" "),
      }))
  );

  const value = layer?.followedPolicyTowerShareId || NONE_KEY;

  return (
    <FormControl variant={"standard"} fullWidth size={"small"}>
      <Select
        value={choices.some((c) => c.key === value) ? value : NONE_KEY}
        onChange={(event) => {
          const selected = event?.target?.value || NONE_KEY;
          dispatch(
            pricingActions.updateLayer({
              layerId: layer?.id,
              values: {
                followedPolicyTowerShareId:
                  selected === NONE_KEY ? null : selected,
              },
            })
          );
        }}
        // The surrounding row is click-to-select; keep those clicks local.
        onClick={(event) => event.stopPropagation()}
        disabled={disabled || !layer?.id}
        displayEmpty
      >
        <MenuItem value={NONE_KEY}>{"None"}</MenuItem>
        {choices.map(({ key, name }) => (
          <MenuItem key={key} value={key}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FollowedPolicyTableSelect;
